import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { BusinessKycPanel } from "@/components/applications/BusinessKycPanel";
import { FinancialDocsPanel } from "@/components/applications/FinancialDocsPanel";
import { ShareholdersPanel } from "@/components/applications/ShareholdersPanel";
import { SupportingDocsPanel } from "@/components/applications/SupportingDocsPanel";
import { StatusTimeline } from "@/components/applications/StatusTimeline";
import { PageHeader } from "@/components/layout/PageHeader";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { cn } from "@/lib/cn";
import {
  STATUS_DOT_CLASS,
  STATUS_TONE_CLASS,
  isCreditOfferApproved,
  statusToneFor,
} from "@/lib/application-status";
import { ApiError } from "@/lib/api/client";
import { getApplication } from "@/services/applications";
import type { ApplicationDetail } from "@/types/application";
import { DOCUMENT_CATEGORY_LABELS } from "@/types/application";

type TabId =
  | "details"
  | "financial_docs"
  | "business_kyc"
  | "shareholders"
  | "supporting_docs";

const TABS: { id: TabId; label: string }[] = [
  { id: "details", label: "Lead details" },
  { id: "financial_docs", label: DOCUMENT_CATEGORY_LABELS.financial_docs },
  { id: "business_kyc", label: DOCUMENT_CATEGORY_LABELS.business_kyc },
  { id: "shareholders", label: "Shareholders" },
  { id: "supporting_docs", label: "Supporting Documents" },
];

const TAB_IDS = new Set<string>(TABS.map((t) => t.id));

function parseTab(value: string | null): TabId {
  return value && TAB_IDS.has(value) ? (value as TabId) : "details";
}

export function ApplicationDetailPage() {
  const { uuid = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [application, setApplication] = useState<ApplicationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const tab = parseTab(searchParams.get("tab"));

  const setTab = (next: TabId) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === "details") {
          params.delete("tab");
        } else {
          params.set("tab", next);
        }
        return params;
      },
      { replace: true },
    );
  };

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!uuid) return;
    if (!opts?.silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const data = await getApplication(uuid);
      setApplication(data);
    } catch (err) {
      if (opts?.silent) return;
      setApplication(null);
      setError(err instanceof ApiError ? err.message : "Unable to load application");
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [uuid]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner className="size-7" />
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="space-y-4">
        <Alert>{error ?? "Application not found"}</Alert>
        <Link to="/applications">
          <Button variant="secondary">Back to applications</Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow={application.loan_identifier || "Application"}
        title={application.company_name || "Untitled company"}
        description={`${application.merchant_name} · ${application.merchant_email || application.email}`}
        actions={
          <Link to="/applications">
            <Button variant="secondary">All applications</Button>
          </Link>
        }
      />

      <StatusBanner
        status={application.status}
        label={application.status_display}
        remarks={application.status_remarks}
      />

      <div className="mb-6 flex flex-wrap gap-2 border-b border-line pb-3">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={cn(
              "rounded-lg px-3 py-2 text-sm font-medium transition",
              tab === item.id
                ? "bg-brand-gradient text-white shadow-sm"
                : "bg-white text-muted ring-1 ring-line hover:text-ink",
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === "details" ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(18rem,0.9fr)]">
          <section className="rounded-2xl border border-line bg-surface-raised p-5 sm:p-6">
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-ink">Lead details</h2>
              <p className="mt-1 text-sm text-muted">Read-only lead information for this application.</p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <InfoCard label="Company name" value={application.company_name || "—"} />
              <InfoCard label="Company type" value={application.company_type_label || "—"} />
              <InfoCard label="Company website" value={application.company_website || "—"} />
              <InfoCard label="Owner" value={application.merchant_name} />
              <InfoCard
                label="Mobile"
                value={`${application.country_code} ${application.phone_number}`}
              />
              <InfoCard
                label="Email"
                value={application.merchant_email || application.email}
              />
            </div>
          </section>

          <StatusTimeline
            currentStatus={application.status}
            currentLabel={application.status_display}
            entries={application.status_timeline}
          />
        </div>
      ) : null}

      {tab === "financial_docs" ? (
        <FinancialDocsPanel
          application={application}
          onChanged={() => load({ silent: true })}
        />
      ) : null}

      {tab === "business_kyc" ? (
        <BusinessKycPanel
          applicationUuid={application.uuid}
          locked={isCreditOfferApproved(application.status)}
          defaultCompanyName={application.company_name}
          defaultIndustrySectorId={application.company_type}
          onDocumentsChanged={() => load({ silent: true })}
        />
      ) : null}

      {tab === "shareholders" ? (
        <ShareholdersPanel
          applicationUuid={application.uuid}
          locked={isCreditOfferApproved(application.status)}
          onChanged={() => load({ silent: true })}
        />
      ) : null}

      {tab === "supporting_docs" ? (
        <SupportingDocsPanel
          applicationUuid={application.uuid}
          canDelete={application.financial_docs_editable !== false}
          onChanged={() => load({ silent: true })}
        />
      ) : null}
    </div>
  );
}

function InfoCard({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-xl border border-line bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</p>
      <div className="mt-2 text-sm font-medium text-ink">{value}</div>
    </div>
  );
}

function StatusBanner({
  status,
  label,
  remarks,
}: {
  status: string;
  label: string;
  remarks?: string | null;
}) {
  const tone = statusToneFor(status);
  const trimmedRemarks = remarks?.trim();

  return (
    <div
      className={cn(
        "mb-6 rounded-2xl border px-4 py-3.5 ring-1 ring-inset sm:px-5",
        STATUS_TONE_CLASS[tone],
      )}
    >
      <div className="flex items-center gap-3">
        <span
          className={cn("size-2.5 shrink-0 rounded-full", STATUS_DOT_CLASS[tone])}
          aria-hidden
        />
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] opacity-70">
            Current status
          </p>
          <p className="truncate text-base font-semibold sm:text-lg">{label}</p>
        </div>
      </div>
      {trimmedRemarks ? (
        <div className="mt-3 rounded-xl bg-white/70 px-3 py-2.5 text-sm text-ink-soft ring-1 ring-black/5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
            Remarks
          </p>
          <p className="mt-1 whitespace-pre-wrap">{trimmedRemarks}</p>
        </div>
      ) : null}
    </div>
  );
}
